import { animate, useMotionValue, useReducedMotion } from 'motion/react';
import { useEffect, useRef } from 'react';

import {
  BODY_KEYFRAMES,
  BODY_TIMES,
  FINE_POINTER_QUERY,
  FLUTTER_DURATION,
  LOGO_EASE as EASE,
  WING_KEYFRAMES,
  WING_TIMES,
} from './logo-motion';

const CONTINUOUS_REPEAT_DELAY = 0.6;

const HOVER_STREAK_THRESHOLD = 4;
const HOVER_STREAK_RESET_MS = 4000;

export const FLUTTER_DURATION_MS = FLUTTER_DURATION * 1000;

type UseLogoFlutterOptions = {
  continuous?: boolean;
  onHoverStreak?: () => void;
};

export const useLogoFlutter = ({
  continuous = false,
  onHoverStreak,
}: UseLogoFlutterOptions = {}) => {
  const bodyY = useMotionValue(0);
  const wingAngle = useMotionValue(0);
  const reduceMotion = useReducedMotion();

  const flutterCountRef = useRef(0);
  const lastFlutterAtRef = useRef(0);

  const playFlutter = () => {
    if (reduceMotion) return;
    if (
      typeof window !== 'undefined' &&
      !window.matchMedia(FINE_POINTER_QUERY).matches
    ) {
      return;
    }

    if (onHoverStreak) {
      const now = Date.now();
      if (now - lastFlutterAtRef.current > HOVER_STREAK_RESET_MS) {
        flutterCountRef.current = 0;
      }
      lastFlutterAtRef.current = now;
      flutterCountRef.current += 1;
      if (flutterCountRef.current >= HOVER_STREAK_THRESHOLD) {
        flutterCountRef.current = 0;
        onHoverStreak();
        return;
      }
    }

    animate(wingAngle, WING_KEYFRAMES, {
      duration: FLUTTER_DURATION,
      times: WING_TIMES,
      ease: EASE,
    });
    animate(bodyY, BODY_KEYFRAMES, {
      duration: FLUTTER_DURATION,
      times: BODY_TIMES,
      ease: EASE,
    });
  };

  useEffect(() => {
    if (!continuous || reduceMotion) return;
    const wing = animate(wingAngle, WING_KEYFRAMES, {
      duration: FLUTTER_DURATION,
      times: WING_TIMES,
      ease: EASE,
      repeat: Infinity,
      repeatDelay: CONTINUOUS_REPEAT_DELAY,
    });
    const body = animate(bodyY, BODY_KEYFRAMES, {
      duration: FLUTTER_DURATION,
      times: BODY_TIMES,
      ease: EASE,
      repeat: Infinity,
      repeatDelay: CONTINUOUS_REPEAT_DELAY,
    });
    return () => {
      wing.stop();
      body.stop();
      animate(wingAngle, 0, { duration: 0.3, ease: EASE });
      animate(bodyY, 0, { duration: 0.3, ease: EASE });
    };
  }, [continuous, reduceMotion, wingAngle, bodyY]);

  return { bodyY, wingAngle, playFlutter };
};
